/* eslint-disable prettier/prettier */
/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useMemo, useState } from "react";

import { useSessionStorage } from "usehooks-ts";


import { ROUTES_LIST } from "Router/Routes";
import { getFavoriteList } from "lib/v3/workspacesApi";
import { useAuth, usePrimaryEvents, useRole } from "state";

import { ContentListIcon } from "components/icons";
import {
  EventConfigIcon,
  ItineraryIcon,
  LiveTrackingIcon,
  PlanningIcon,
  RoomTypeIcon,
  // SettingsIcon,
  UsersListIcon,
  VehiclesIcon,
} from "components/icons/Sidebar";
import PresetsIcon from "components/icons/Sidebar/PresetsIcon";
import WorkspacesIcon from "components/icons/Sidebar/WorkspacesIcon";
import TicketsSimple from "components/icons/TicketsSimple";

import { getEnvs } from "state/EnvironmentsContext/EnvContext";
import FEATURE_FLAGS from "state/FeaturesFlagContext/flags";

// Define types and interfaces
interface Page {
  name: string;
  slug: string;
  id?: string;
  icon?: React.ComponentType;
  feature_flag?: string;
  permissionKey: string;
  isOwnerOnly?: boolean;
}

interface Group {
  title: string;
  isOpen: boolean;
  pages: Page[];
  icon: React.ComponentType<any>;
  id?: string;
}

interface Workspace {
  name: string;
  permissionKey: string;
  slug: string;
}

const getUnityShowAddOns = (): boolean => getEnvs((state) => state.meta_title) === "UNITY";

const getEventAddons = (): Page => ({
  name: "Event Add-ons",
  permissionKey: "EVENT_ADD_ONS",
  slug: ROUTES_LIST.EVENT_ADD_ONS.routeMap?.base,
  id: "event-addons",
});

const WORKSPACE_LIST: Page[] = [
  {
    name: "All Workspaces ",
    permissionKey: "WORKSPACES",
    slug: ROUTES_LIST.WORKSPACES.routeMap.pagination(1),
  },
];

const ADMINISTRATION_LIST: Page[] = [
  {
    name: "Admins",
    slug: ROUTES_LIST.ADMINISTRATION.routeMap?.admins.pagination(1),
    permissionKey: "ADMINISTRATION",
  },
  {
    name: "Admin Roles",
    slug: ROUTES_LIST.PRESETS.routeMap?.roles.pagination(1),
    permissionKey: "PRESETS",
  },
  {
    name: "Teams",
    slug: ROUTES_LIST.ADMINISTRATION.routeMap?.teams.base,
    permissionKey: "ADMINISTRATION",
  },
  {
    name: "Organizer App Roles",
    slug: ROUTES_LIST.STAFF_ROLES.routeMap.pagination(1),
    permissionKey: "STAFF_ROLES",
  },
];

const PRESETS_LIST: Page[] = [
  {
    name: "Event Presets",
    slug: ROUTES_LIST.PRESETS.routeMap?.event.base,
    permissionKey: "PRESETS",
  },
  {
    name: "Classifications",
    slug: ROUTES_LIST.PRESETS.routeMap?.classifications.base,
    feature_flag: FEATURE_FLAGS.classification,
    permissionKey: "PRESETS",
  },
  {
    name: "Theming",
    slug: ROUTES_LIST.PRESETS.routeMap?.theming.base,
    permissionKey: "PRESETS",
  },
  {
    name: "Airports",
    slug: ROUTES_LIST.PRESETS.routeMap?.airport.pagination(1),
    permissionKey: "PRESETS",
  },
  {
    name: "Messaging Config",
    slug: ROUTES_LIST.PRESETS.routeMap?.reminders.base,
    permissionKey: "PRESETS",
  },
];

const CONTENT_LIST: Page[] = [
  {
    name: "Explore",
    slug: ROUTES_LIST.EXPLORE.routeMap?.base,
    id: "explore",
    permissionKey: "EXPLORE",
  },
  {
    name: "Discover",
    slug: ROUTES_LIST.DISCOVER.routeMap?.base,
    id: "discover",
    permissionKey: "DISCOVER",
  },
  {
    name: "Itinerary",
    slug: ROUTES_LIST.ITINERARY.routeMap?.base,
    icon: ItineraryIcon,
    id: "itinerary",
    permissionKey: "ITINERARY",
  },
  {
    name: "Send Updates",
    slug: ROUTES_LIST.UPDATES.routeMap?.base,
    id: "send-updates",
    permissionKey: "UPDATES",
  },
  {
    name: "In-App Guidance",
    slug: ROUTES_LIST.IN_APP_GUIDANCE.routeMap?.base,
    id: "in-app-guidance",
    feature_flag: FEATURE_FLAGS.in_app_guidance,
    permissionKey: "IN_APP_GUIDANCE",
  },
  {
    name: "Directory",
    slug: ROUTES_LIST.DIRECTORY.routeMap?.base,
    id: "directory",
    permissionKey: "DIRECTORY",
  },
];

const PLANNING_LIST: Page[] = [
  {
    name: "Sessions",
    slug: ROUTES_LIST.SESSIONS.routeMap?.base,
    id: "sessions",
    permissionKey: "SESSIONS",
  },
  {
    name: "Venues",
    slug: ROUTES_LIST.VENUES.routeMap?.base,
    id: "venues",
    permissionKey: "VENUES",
  },
];

const STATISTICS_LIST: Page[] = [
  {
    name: "Analytics",
    slug: ROUTES_LIST.ANALYTICS.routeMap?.base,
    feature_flag: FEATURE_FLAGS.generalStatistics,
    id: "analytics",
    permissionKey: "ANALYTICS",
  },
  {
    name: "Attendees Reports",
    slug: ROUTES_LIST.USER_REPORTS.routeMap?.base,
    id: "attendees-reports",
    permissionKey: "USER_REPORTS",
  },
  {
    name: "Store Metrics",
    slug: ROUTES_LIST.STORE_STATISTICS.routeMap?.base,
    id: "store-metrics",
    permissionKey: "STORE_STATISTICS",
  },
    {
    name: "Order Metrics",
    slug: ROUTES_LIST.ORDER_STATISTICS.routeMap?.base,
    id: "order-metrics",
    permissionKey: "ORDER_STATISTICS",
  }
];

const EVENT_CONFIG_LIST: Page[] = [
  {
    name: "Event Info",
    slug: ROUTES_LIST.EVENT_INFO.routeMap?.base,
    id: "event-info",
    permissionKey: "EVENT_INFO",
  },
  {
    name: "Packages",
    slug: ROUTES_LIST.PACKAGES.routeMap?.base,
    id: "packages",
    permissionKey: "PACKAGES",
  },
  {
    name: "Admission Settings",
    slug: ROUTES_LIST.ADMISSION_SETTINGS.routeMap?.base,
    id: "admission-settings",
    permissionKey: "ADMISSION_SETTINGS",
  },
  {
    name: "Messaging Config",
    slug: ROUTES_LIST.EVENT_COPIES.routeMap?.base,
    id: "messaging-config",
    permissionKey: "EVENT_COPIES",
  },
  {
    name: "Ticket Type",
    slug: ROUTES_LIST.TICKETS.routeMap?.base,
    id: "ticket-type",
    permissionKey: "TICKETS",
  },
  {
    name: "Forms",
    slug: ROUTES_LIST.APPLICATION_FORMS.routeMap?.base,
    id: "application-forms",
    permissionKey: "APPLICATION_FORMS",
  },
  {
    name: "Web Pages",
    slug: ROUTES_LIST.WEB_PAGES.routeMap?.base,
    id: "web-pages",
    permissionKey: "WEB_PAGES",
  },
  {
    name: "App Config",
    slug: ROUTES_LIST.APP_CONFIG.routeMap?.base,
    feature_flag: FEATURE_FLAGS.app_config,
    id: "app-config",
    permissionKey: "APP_CONFIG",
  },
  {
    name: "Access Config",
    slug: ROUTES_LIST.ACCESS_CONFIG.routeMap?.base,
    id: "access-config",
    permissionKey: "ACCESS_CONFIG",
  },
    {
    name: "Theming",
    slug: ROUTES_LIST.THEMING.routeMap?.base,
    feature_flag: FEATURE_FLAGS.eventTheming,
    id: "event-theming",
    permissionKey: "THEMING",
  },
  ...(getUnityShowAddOns() ? [getEventAddons()] : []),
];

const BADGES_LIST: Page[] = [
  {
    name: "Orders",
    slug: ROUTES_LIST.ORDERS.routeMap?.base,
    id: "orders",
    permissionKey: "ORDERS",
  },
  {
    name: "Badge Types",
    slug: ROUTES_LIST.BADGE_TYPES.routeMap?.base,
    id: "badge_types",
    permissionKey: "BADGE_TYPES",
  },
  {
    name: "Badge Groups",
    slug: ROUTES_LIST.BADGE_GROUPS.routeMap?.base,
    id: "badge_groups",
    permissionKey: "BADGE_GROUPS",
  },
  {
    name: "Coupons",
    slug: ROUTES_LIST.COUPONS.routeMap?.base,
    id: "coupons",
    permissionKey: "COUPONS",
  },
  {
    name: "Coupon Categories",
    slug: ROUTES_LIST.COUPON_CATEGORIES.routeMap?.base,
    id: "coupon_categories",
    permissionKey: "COUPON_CATEGORIES",
  },
  {
    name: "Affiliate Links",
    slug: ROUTES_LIST.AFFILIATE_LINKS.routeMap?.base,
    id: "affiliate_links",
    permissionKey: "AFFILIATE_LINKS",
  },
  {
    name: "Refund Reasons",
    slug: ROUTES_LIST.REFUND_REASONS.routeMap?.base,
    id: "refund_reasons",
    permissionKey: "REFUND_REASONS",
  },
  {
    name: "Return Reasons",
    slug: ROUTES_LIST.RETURN_REASONS.routeMap?.base,
    id: "return_reasons",
    permissionKey: "RETURN_REASONS",
  },
  {
    name: "Payment Methods",
    slug: ROUTES_LIST.PAYMENT_METHODS.routeMap?.base,
    id: "payment_methods",
    permissionKey: "PAYMENT_METHODS",
  },
  {
    name: "Store Settings",
    slug: ROUTES_LIST.STORE_SETTINGS.routeMap?.base,
    id: "store_settings",
    permissionKey: "STORE_SETTINGS",
  },
];

const REGISTRATION_LIST: Page[] = [
  {
    name: "Attendees",
    slug: ROUTES_LIST.USERS.routeMap?.base,
    id: "attendees",
    permissionKey: "USERS",
  },
  {
    name: "Invitees",
    slug: ROUTES_LIST.INVITEES.routeMap?.base,
    id: "invitees",
    permissionKey: "INVITEES",
  },
  {
    name: "Points of Contact",
    slug: ROUTES_LIST.CONTACTS.routeMap?.base,
    id: "points-of-contact",
    permissionKey: "CONTACTS",
  },
  {
    name: "Staff",
    slug: ROUTES_LIST.STAFF.routeMap?.base,
    id: "staff",
    permissionKey: "STAFF",
  },
  {
    name: "Chat Groups",
    slug: ROUTES_LIST.CHATS.routeMap?.base,
    id: "chat-groups",
    permissionKey: "CHATS",
  },
  {
    name: "Applications",
    slug: ROUTES_LIST.APPLICATIONS.routeMap?.base,
    id: "applications",
    permissionKey: "APPLICATIONS",
  },
];

const ACCOMMODATION_LIST: Page[] = [
  {
    name: "Stay",
    slug: ROUTES_LIST.STAYS.routeMap?.base,
    id: "stay",
    permissionKey: "STAYS",
  },
  {
    name: "Room Type",
    slug: ROUTES_LIST.ROOMS.routeMap?.base,
    id: "room-type",
    permissionKey: "ROOMS",
  },
  {
    name: "Reservation",
    slug: ROUTES_LIST.RESERVATIONS.routeMap?.base,
    id: "reservation",
    permissionKey: "RESERVATIONS",
  },
];

const TRANSPORTATION_LIST: Page[] = [
  {
    name: "Journeys",
    slug: ROUTES_LIST.JOURNEYS.routeMap?.base,
    id: "journeys",
    permissionKey: "JOURNEYS",
  },
  {
    name: "Convoys",
    slug: ROUTES_LIST.CONVOYS.routeMap?.base,
    id: "convoys",
    permissionKey: "CONVOYS",
  },
  {
    name: "Drivers",
    slug: ROUTES_LIST.DRIVERS.routeMap?.base,
    id: "drivers",
    permissionKey: "DRIVERS",
  },
  {
    name: "Vehicles",
    slug: ROUTES_LIST.VEHICLES.routeMap?.base,
    id: "vehicles",
    permissionKey: "VEHICLES",
  },
  {
    name: "Dedicated Vehicles",
    slug: ROUTES_LIST.DEDICATED.routeMap?.base,
    id: "dedicated-vehicles",
    permissionKey: "DEDICATED",
  },
  {
    name: "Live Tracking",
    slug: ROUTES_LIST.LIVE_TRACKING.routeMap?.base,
    icon: LiveTrackingIcon,
    id: "live-tracking",
    permissionKey: "LIVE_TRACKING",
  },
];


// const SETTINGS_LIST: Page[] = [
//   {
//     name: "Platform",
//     slug: ROUTES_LIST.SETTINGS.routeMap?.base,
//     id: "platform",
//     permissionKey: "SETTINGS",
//   },
//   // {
//   //   name: "Admin Roles",
//   //   slug: ROUTES_LIST.ROLES.routeMap?.base,
//   // },
//   // {
//   //   name: "Admin Users",
//   //   slug: ROUTES_LIST.ADMINS.routeMap?.base,
//   // },
// ];
// Define other lists...
const GROUPED_LIST: Group[] = [
  {
    title: "Event Config",
    isOpen: false,
    pages: EVENT_CONFIG_LIST,
    icon: EventConfigIcon,
    id: "event-config",
  },
  {
    title: "Badges",
    isOpen: false,
    pages: BADGES_LIST,
    icon: TicketsSimple,
    id: "badges",
  },
  {
    title: "Content",
    isOpen: false,
    pages: CONTENT_LIST,
    icon: ContentListIcon,
    id: "content",
  },
  {
    title: "Planning",
    isOpen: false,
    pages: PLANNING_LIST,
    icon: PlanningIcon,
    id: "planning",
  },

  {
    title: "Registration",
    isOpen: false,
    pages: REGISTRATION_LIST,
    icon: UsersListIcon,
    id: "registration",
  },
  {
    title: "Transportation",
    isOpen: false,
    pages: TRANSPORTATION_LIST,
    icon: VehiclesIcon,
    id: "transportation",
  },
  {
    title: "Accommodation",
    isOpen: false,
    pages: ACCOMMODATION_LIST,
    icon: RoomTypeIcon,
    id: "accommodation",
  },

  {
    title: "Statistics",
    isOpen: false,
    pages: STATISTICS_LIST,
    icon: PlanningIcon,
    id: "statistics",
  },
  // {
  //   title: "Settings",
  //   isOpen: false,
  //   pages: SETTINGS_LIST,
  //   icon: SettingsIcon,
  //   id: "settings",
  // },
];

const filterGroupedListByPermissions = (groupedList: Group[], authPages: {[x: string]: string[]}): Group[] => {
  return groupedList
    .map((group) => ({
      ...group,
      pages: group.pages.filter((page) => authPages[page.permissionKey || ""]),
    }))
    .filter((group) => group.pages.length > 0);
};

export const useDefaultSidebarList = (): { SidebarList: Group[]; WORKSPACE_LIST: Page[] } => {
  const { hasSelectedEvent } = usePrimaryEvents();
  const [shouldUpdateSidebar, setShouldUpdateSidebar] = useSessionStorage<boolean>("shouldUpdateSidebar", false);
  const [workspaceList, setWorkspaceList] = useState<Workspace[]>([]);
  const { user } = useAuth();
  const [workspaceOpen, setWorkspaceOpen] = useState<boolean>(false);
  const { permissions } = useRole();

  const updateFavoriteList = async () => {
    const res = await getFavoriteList();
    if (res.ok) {
      const favoriteWorkspaceList = res.data;

      const updatedList = favoriteWorkspaceList.map((workspace: { title: string; id: string }) => ({
        name:
          workspace.title.substring(0, 15) +
          (workspace.title.length > 15 ? "..." : ""),
        permissionKey: "WORKSPACES",
        slug:
          ROUTES_LIST?.WORKSPACES.routeMap?.base +
          "/" +
          workspace.id +
          "/events/p/" +
          1,
      }));

      setWorkspaceList(updatedList);
    }
  };

  useEffect(() => {
    if (shouldUpdateSidebar === true) {
      updateFavoriteList();
      setShouldUpdateSidebar(false);
      setWorkspaceOpen(true);
    }
  }, [shouldUpdateSidebar]);

  useEffect(() => {
    updateFavoriteList();
  }, []);

  const ORGANIZATION_LIST: Group[] = useMemo(() => {
    return [
      {
        title: "Workspace",
        isOpen: workspaceOpen,
        pages: [...WORKSPACE_LIST, ...workspaceList],
        icon: WorkspacesIcon,
      },
      ...(user.hasAdministrationAccess
        ? [
            {
              title: "Administration",
              isOpen: false,
              pages: ADMINISTRATION_LIST,
              icon: UsersListIcon,
            },
            {
              title: "Presets",
              isOpen: false,
              pages: PRESETS_LIST,
              icon: PresetsIcon,
            },
          ]
        : []),
    ];
  }, [workspaceList, user.hasAdministrationAccess]);

  const filteredGroupedList: Group[] = useMemo(() => {
    return filterGroupedListByPermissions(GROUPED_LIST, permissions);
  }, [permissions]);

  const SidebarList: Group[] = hasSelectedEvent
    ? filteredGroupedList
    : ORGANIZATION_LIST;

  return { SidebarList, WORKSPACE_LIST };
};

export const FOOTER_LIST: { name: string }[] = [
  {
    name: "Log out",
  },
];